import {
  AtButton,
  AtButtonState,
  AtButtonVariant,
  AtIcon,
  AtLink,
  AtLinkVariant,
  MlFormField,
} from '@curran-catalog/curran-atomic-library'
import { useAuth } from '@hooks/use-auth'
import { useState } from 'react'
import { Cart } from 'types'

type OrderSummaryDefaultProps = {
  promoError: string
  showEstimateTaxInput: boolean
  isSubmitting: boolean
  removePromo: () => Promise<void>
  handlePromoSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  handleCheckoutClick: () => void
  setShowEstimateTaxInput: (show: boolean) => void
  handleEstimateTaxZipSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  cart?: Cart
}

const OrderSummaryDefault = ({
  promoError,
  showEstimateTaxInput,
  isSubmitting,
  removePromo,
  handlePromoSubmit,
  handleCheckoutClick,
  setShowEstimateTaxInput,
  handleEstimateTaxZipSubmit,
  cart,
}: OrderSummaryDefaultProps) => {
  const { user } = useAuth({})
  const [isSubmitLogin, setIsSubmitLogin] = useState(false)
  return (
    <section
      aria-labelledby="summary-heading"
      className="mt-16 bg-subdued px-4 py-6 sm:p-6 lg:col-span-5 lg:mt-0 lg:p-8"
    >
      <h2 id="summary-heading" className="text-lg font-medium text-gray-900">
        Order summary
      </h2>

      <dl className="mt-6 space-y-4">
        <div className="flex items-center justify-between">
          <dt className="text-sm text-gray-600">Subtotal</dt>
          <dd className="text-sm font-medium text-gray-900">
            {!cart || cart.isUpdatingTotals ? <AtIcon type="spinner" size={20} /> : cart.subtotal}
          </dd>
        </div>
        {cart?.discount && (
          <div className="flex items-center justify-between border-t border-outlined-gray pt-4">
            <dt className="flex">
              <span className="text-sm text-gray-600">Discount</span>
              {cart.activePromoCode && (
                <span
                  className="ml-2 rounded-full bg-gray py-0.5 px-2 text-xs tracking-wide text-subdued cursor-pointer"
                  onClick={removePromo}
                >
                  {cart.activePromoCode}
                  <span className="ml-2">&times;</span>
                </span>
              )}
            </dt>
            <dd className="text-sm font-medium text-success">
              {cart.isUpdatingTotals ? <AtIcon type="spinner" size={20} /> : '-' + cart.discount}
            </dd>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-outlined-gray pt-4">
          <dt className="flex items-center text-sm text-gray-600">
            <span>Standard Shipping</span>
          </dt>
          <dd className="text-sm font-medium text-gray-900">
            {!cart || cart.isUpdatingTotals ? <AtIcon type="spinner" size={20} /> : cart.freight}
          </dd>
        </div>
        <div className="flex items-center justify-between border-t border-outlined-gray pt-4">
          <dt className="flex text-sm text-gray-600">
            {cart?.estimatedTax ? (
              <>
                <span>Estimated tax for</span>{' '}
                <AtLink
                  variant={AtLinkVariant.DEFAULT}
                  onClick={() => setShowEstimateTaxInput(!showEstimateTaxInput)}
                  className="font-medium ml-1"
                >
                  {cart.estimatedTaxZip}
                </AtLink>
              </>
            ) : (
              <span>Tax</span>
            )}
          </dt>
          <dd className="text-sm font-medium text-gray-900">
            {!cart || cart.isUpdatingTotals ? (
              <AtIcon type="spinner" size={20} />
            ) : showEstimateTaxInput ? (
              <form className="flex rounded-md -my-1" onSubmit={handleEstimateTaxZipSubmit}>
                <input
                  type="text"
                  placeholder="Zip"
                  name="zip"
                  minLength={5}
                  maxLength={10}
                  pattern="[0-9]*"
                  required
                  className="block w-20 px-2 py-1.5 border border-outlined-gray rounded-none rounded-l-md text-sm text-gray-900  focus:border-indigo-500 focus:ring-indigo-500 focus-within:z-10"
                />
                <button
                  type="submit"
                  className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-outlined-gray px-4 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <AtIcon
                    type="check"
                    direction="down"
                    className="h-4 w-4 text-gray group-hover:text-dark"
                    aria-hidden="true"
                  />
                </button>
              </form>
            ) : cart.estimatedTax ? (
              cart.estimatedTax
            ) : (
              <AtLink
                variant={AtLinkVariant.DEFAULT}
                className="font-medium"
                onClick={() => setShowEstimateTaxInput(true)}
              >
                Estimate
              </AtLink>
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between border-t border-outlined-gray pt-4">
          <dt className="text-base font-medium text-gray-900">Order total</dt>
          <dd className="text-base font-medium text-gray-900">
            {!cart || cart.isUpdatingTotals ? <AtIcon type="spinner" size={20} /> : cart.total}
          </dd>
        </div>
      </dl>

      <form className="mt-10" onSubmit={handlePromoSubmit}>
        <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700">
          Discount code
        </label>
        <div className="mt-1 flex space-x-4 items-start">
          <div className="flex-1">
            <MlFormField
              type="text"
              id="promoCode"
              name="promoCode"
              error={promoError !== ''}
              errorMessage={promoError}
              inputClassName="shadow-sm"
              required
            />
          </div>
          <AtButton
            className="h-[48px] mt-[1px] shadow-sm"
            variant={AtButtonVariant.TERTIARY}
            label="Apply"
            type="submit"
          />
        </div>
      </form>
      <div className="mt-6">
        <AtButton
          className="w-full"
          variant={AtButtonVariant.PRIMARY}
          state={isSubmitting ? AtButtonState.DISABLED : AtButtonState.ENABLED}
          onClick={handleCheckoutClick}
          label={isSubmitting ? 'Loading...' : user ? 'Checkout' : 'Checkout as Guest'}
        />
      </div>
      {!user && (
        <div className="mt-2">
          <AtButton
            className="w-full"
            variant={AtButtonVariant.TERTIARY}
            state={isSubmitLogin ? AtButtonState.DISABLED : AtButtonState.ENABLED}
            onClick={function () {
              location.assign('/login')
              setIsSubmitLogin(true)
            }}
            label={isSubmitLogin ? 'Loading...' : 'Sign in or Create Account'}
          />
        </div>
      )}
    </section>
  )
}

export default OrderSummaryDefault
